// Budget utilisation maths for the "time log saved" popup and the project Target % field.
//
//   individual % = (hours x rate) / budget          what one person has used of the budget
//   project %    = total cost / budget              how much of the budget the project has used
//   target cost  = budget x target %                the spend the team must stay within
//   target used  = total cost / target cost         above 100 the incentive is lost
//
// Everything here is pure so it can be unit tested and reused by the popup, the
// project form and (later) whatever the API returns.

export const DEFAULT_TARGET_PERCENT = 70;
export const MIN_TARGET_PERCENT = 1;
export const MAX_TARGET_PERCENT = 100;

const toNumber = (value) => {
  const n = typeof value === "string" ? Number(value.replace(/,/g, "")) : Number(value);
  return Number.isFinite(n) ? n : 0;
};

// part / whole as a percentage; 0 when there is nothing to divide by.
export const percentOf = (part, whole) => {
  const w = toNumber(whole);
  return w > 0 ? (toNumber(part) / w) * 100 : 0;
};

export const getTargetCost = (budget, targetPercent = DEFAULT_TARGET_PERCENT) =>
  (toNumber(budget) * toNumber(targetPercent)) / 100;

// Whole-number percentage text, e.g. 85.7 -> "86%".
export const formatPercent = (value) => `${Math.round(toNumber(value))}%`;

// "Exceeded by" / "left" figures. A real overshoot that rounds to 0 reads "<1%"
// instead of a misleading "0%".
export const formatDelta = (value) => {
  const n = toNumber(value);
  if (n > 0 && Math.round(n) === 0) return "<1%";
  return `${Math.round(n)}%`;
};

const rupees = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 });
export const formatRupees = (amount) => `₹${rupees.format(Math.round(toNumber(amount)))}`;

// 150 -> "2h 30m", 180 -> "3h 00m"
export const formatDuration = (totalMinutes) => {
  const minutes = Math.max(0, Math.round(toNumber(totalMinutes)));
  return `${Math.floor(minutes / 60)}h ${String(minutes % 60).padStart(2, "0")}m`;
};

// 3000 -> "50h", 3030 -> "50h 30m" (used for big headline numbers)
export const formatHoursCompact = (totalMinutes) => {
  const minutes = Math.max(0, Math.round(toNumber(totalMinutes)));
  return minutes % 60 === 0 ? `${minutes / 60}h` : formatDuration(minutes);
};

// What the popup needs, from first principles (money in, percentages out).
//   myCost       cost of the logged-in user's hours on the project (hours x rate)
//   projectCost  cost of everyone's hours on the project
export const calculateUtilisation = ({ budget, targetPercent = DEFAULT_TARGET_PERCENT, myCost, projectCost }) => {
  const targetCost = getTargetCost(budget, targetPercent);
  return {
    individualPct: percentOf(myCost, budget),
    projectPct: percentOf(projectCost, budget),
    targetPercent: toNumber(targetPercent),
    targetCost,
    targetUsedPct: percentOf(projectCost, targetCost),
  };
};

// Status flags derived from the two headline percentages. Decisions use the raw
// values, never the rounded text, so 100.4% is still "exceeded".
export const getBudgetHealth = ({ projectPct, targetUsedPct }) => {
  const project = toNumber(projectPct);
  const target = toNumber(targetUsedPct);
  const isOverBudget = project > 100;
  const isTargetAchieved = target <= 100;
  return {
    isOverBudget,
    isWithinBudget: !isOverBudget,
    isTargetAchieved,
    budgetLeftPct: isOverBudget ? 0 : 100 - project,
    exceededByPct: isOverBudget ? project - 100 : 0,
    targetLeftPct: isTargetAchieved ? 100 - target : 0,
    targetExceededByPct: isTargetAchieved ? 0 : target - 100,
  };
};

// A bar never grows past its track, but its label keeps the real number.
export const clampBarWidth = (value) => Math.min(100, Math.max(0, toNumber(value)));

// ---- Target % field ----------------------------------------------------------

// Returns an error message, or null when the value is acceptable.
export const validateTargetPercent = (value) => {
  if (value === "" || value === null || value === undefined) return "Target % is required";
  const n = Number(value);
  if (!Number.isFinite(n)) return "Enter a number";
  if (n < MIN_TARGET_PERCENT || n > MAX_TARGET_PERCENT) {
    return `Target % must be between ${MIN_TARGET_PERCENT} and ${MAX_TARGET_PERCENT}`;
  }
  return null;
};
